import { useRef } from 'react';
import { Button } from '@/components/ui/button';
import { DollarSign, Zap, Upload, TextIcon } from 'lucide-react';
import Layout from '@/layouts/_layout';
import Seo from '@/layouts/_seo';
import type { NextPageWithLayout } from '@/types';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import Image from 'next/image';
import LottieBanner from '@/components/ui/lottie-banner';
import AnchorLink from '@/components/ui/links/anchor-link';
import routes from '@/config/routes';
import ConsistentCharactersImage from '@/assets/images/consistent-characters-3.png';
import ConsistentWomanImage from '@/assets/images/consistent-woman.png';
import ProductSearch from '@/components/product/product-search';
import ProductSlider from '@/components/product/product-slider';
import { useQuery } from 'react-query';
import {
  searchFeaturedProducts,
  searchPortraitPrompts,
  searchLogoIconPrompts,
  searchPopularChatGPTPrompts,
  searchNewProducts,
  searchArtIllustrationPrompts,
} from '@/data/client/typesense-client';
import TypesSection from '@/components/home/types-section';
import { LightningBoltIcon } from '@/components/icons/lightning-bolt-icon';
import { SparklesIcon } from '@/components/icons/sparkles-icon';
import AIModelsList from '@/components/common/ai-models-list';
import CategoryPills from '@/components/common/category-pills';
import { SiteLinksSearchBoxJsonLd, BreadcrumbJsonLd } from 'next-seo';
import SectionContainer from '@/components/home/section-container';

export const getServerSideProps = async ({ locale }: { locale: string }) => {
  return {
    props: {
      ...(await serverSideTranslations(locale, ['common'])),
    },
  };
};

const Home: NextPageWithLayout = () => {
  const searchRef = useRef<HTMLInputElement>(null);

  const { data: featuredProducts = [] } = useQuery(
    ['featured_products', { limit: 15 }],
    () => searchFeaturedProducts({ limit: 15 }),
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    }
  );
  const { data: newProducts = [] } = useQuery(
    ['new_products', { limit: 10 }],
    () => searchNewProducts({ limit: 10 }),
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    }
  );
  const { data: popularChatGPTPrompts = [] } = useQuery(
    ['popular_chatgpt_prompts', { limit: 10 }],
    () => searchPopularChatGPTPrompts({ limit: 10 }),
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    }
  );
  const { data: artIllustrationPrompts = [] } = useQuery(
    ['art_illustration_prompts', { limit: 10 }],
    () => searchArtIllustrationPrompts({ limit: 10 }),
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    }
  );
  const { data: logoIconPrompts = [] } = useQuery(
    ['logo_icon_prompts', { limit: 10 }],
    () => searchLogoIconPrompts({ limit: 10 }),
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    }
  );
  const { data: portraitPrompts = [] } = useQuery(
    ['portrait_prompts', { limit: 10 }],
    () => searchPortraitPrompts({ limit: 10 }),
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    }
  );

  return (
    <>
      <SiteLinksSearchBoxJsonLd
        url="https://promptsclub.com"
        potentialActions={[
          {
            target: 'https://promptsclub.com/products?search',
            queryInput: 'search_term_string',
          },
        ]}
      />

      <BreadcrumbJsonLd
        itemListElements={[
          {
            position: 1,
            name: 'Sell',
            item: 'https://promptsclub.com/become-a-seller',
          },
          {
            position: 2,
            name: 'Marketplace',
            item: 'https://promptsclub.com/products',
          },
          {
            position: 3,
            name: 'ChatGPT prompts',
            item: 'https://promptsclub.com/products?aiModels=gpt',
          },
          {
            position: 4,
            name: 'Login',
            item: 'https://promptsclub.com/profile',
          },
        ]}
      />

      <Seo
        title="AI Prompt & Model Marketplace"
        description="Find consistent Prompts & Models. Discover generative AI models like Midjourney, ChatGPT, Flux AI, LoRA and many more, and speed up your Project's success by 10x. Generate stunning AI photos and videos, explore premium prompts, or sell your own."
        url="/"
      />
      <section className="relative flex w-full items-center justify-center bg-transparent py-12 sm:py-16 md:py-24 md:pt-10 md:pb-10">
        <div className="container relative z-10 px-4 md:px-6">
          <div className="flex flex-col items-center justify-between gap-8 md:flex-row">
            <div className="w-full text-center md:text-left xl:w-3/5">
              <h1 className="mt-4 block text-3xl font-bold text-gray-900 dark:text-gray-100 sm:text-5xl md:text-6xl">
                Find{' '}
                <span className="relative mb-2 inline-block sm:mb-4">
                  <span className="relative inline-flex items-center px-2">
                    <span className="dark:via-purple-500/15 absolute -inset-1 rotate-1 -skew-y-3 rounded-tl-xl rounded-br-xl bg-gradient-to-r from-brand/10 to-purple-500/5 dark:from-brand/20 dark:to-brand/20 dark:shadow-[inset_0_0_15px_rgba(139,92,246,0.05)] dark:backdrop-blur-[2px]"></span>
                    <span className="absolute -inset-1 rotate-1 -skew-y-3 rounded-tl-xl rounded-br-xl bg-gradient-to-r from-transparent to-transparent dark:from-brand/5 dark:to-purple-500/5 dark:blur-sm"></span>
                    <span className="relative text-4xl font-semibold tracking-tight dark:text-gray-50 sm:text-6xl md:text-7xl">
                      consistent
                    </span>
                  </span>{' '}
                  <span className="text-3xl font-bold sm:text-5xl md:text-6xl">
                    AI
                  </span>
                </span>
                <span className="group relative block">
                  <span className="group/gen relative inline-flex font-semibold">
                    <span className="relative z-10 animate-[gradient-loop_2s_linear_infinite] bg-gradient-to-r from-[#4D4DFF] via-purple-500 to-[#4D4DFF] bg-[length:200%_auto] bg-clip-text pb-2 text-transparent drop-shadow-md dark:animate-gradient-xy dark:bg-gradient-to-r dark:from-brand dark:via-purple-500 dark:to-brand dark:bg-[length:200%_auto] dark:bg-clip-text dark:text-transparent dark:drop-shadow-md">
                      <span className="text-brand">Prompts &</span> Models
                    </span>
                  </span>
                  <br />
                  ready to use
                </span>
              </h1>
              <div className="my-8 flex flex-col items-start gap-6 text-base tracking-tight sm:flex-row sm:items-center sm:gap-14 sm:text-lg md:my-6">
                <div className="flex items-center gap-3.5 transition-colors hover:text-gray-900 dark:hover:text-gray-100">
                  <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-gradient-to-br from-brand/20 to-purple-500/10 shadow-[0_0_0_1px_rgba(0,0,0,0.02)] dark:from-brand/30 dark:to-purple-500/20 dark:shadow-[0_0_0_1px_rgba(255,255,255,0.05)] sm:h-12 sm:w-12">
                    <SparklesIcon className="h-5 w-5 text-brand/90 sm:h-6 sm:w-6" />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-default-600 dark:text-gray-100 sm:text-base">
                      Find Prompt Templates
                    </span>
                    {/* <span className="text-xs text-gray-500 dark:text-gray-400 sm:text-sm">
                      Access prompts curated by industry experts
                    </span> */}
                  </div>
                </div>
                <div className="invisible hidden h-4 w-[1px] rotate-12 bg-gray-200/70 dark:bg-gray-700/50 sm:block"></div>
                <div className="flex items-center gap-3.5 transition-colors hover:text-gray-900 dark:hover:text-gray-100">
                  <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-gradient-to-br from-brand/20 to-purple-500/10 shadow-[0_0_0_1px_rgba(0,0,0,0.02)] dark:from-brand/30 dark:to-purple-500/20 dark:shadow-[0_0_0_1px_rgba(255,255,255,0.05)] sm:h-12 sm:w-12">
                    <LightningBoltIcon className="h-5 w-5 text-brand/90 sm:h-6 sm:w-6" />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-default-600 dark:text-gray-100 sm:text-base">
                      Try Models (LoRA, GPT, etc.)
                    </span>
                    {/* <span className="text-xs text-gray-500 dark:text-gray-400 sm:text-sm">
                      Discover industry-leading AI like Flux & ChatGPT
                    </span> */}
                  </div>
                </div>
              </div>
              <div className="mt-8">
                <ProductSearch searchRef={searchRef} hideTabs={true} />
              </div>
              <AIModelsList className="mt-8 hidden sm:mt-12 md:block" />
            </div>
            <div className="hidden w-full items-center justify-center p-4 sm:p-6 md:w-1/2 md:p-8 xl:flex">
              <div className="flex max-w-[500px] flex-col items-center">
                <LottieBanner />
                <CategoryPills className="mt-6" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="my-8 flex w-full items-center justify-center">
        <div className="relative px-4 xl:container md:px-6">
          <div className="absolute inset-0 rounded-3xl bg-gradient-to-b from-brand/5 to-transparent dark:from-brand/10 dark:to-transparent" />
          <div className="relative py-12 text-center">
            <div className="-mt-8 mb-2 flex justify-center">
              <div className="relative inline-flex items-center px-2">
                <span className="absolute -inset-1 rotate-1 -skew-y-3 rounded-tl-xl rounded-br-xl bg-gradient-to-r from-brand/10 to-purple-500/5 dark:from-brand/20 dark:to-brand/20 dark:shadow-[inset_0_0_15px_rgba(139,92,246,0.05)] dark:backdrop-blur-[2px]"></span>
                <span className="absolute -inset-1 rotate-1 -skew-y-3 rounded-tl-xl rounded-br-xl bg-gradient-to-r from-transparent to-transparent dark:from-brand/5 dark:to-purple-500/5 dark:blur-sm"></span>
                <span className="dark:text-brand-light relative text-3xl font-black tracking-tight text-brand sm:text-4xl">
                  AI Generation
                </span>
              </div>
            </div>
            <h2 className="mb-3 text-4xl font-bold text-gray-900 dark:text-gray-100 md:text-5xl lg:text-6xl">
              Made Simple
            </h2>
            <p className="mx-auto max-w-2xl text-gray-600 dark:text-gray-400 md:text-lg">
              With Ready-To-Use Prompts and AI Models
            </p>
          </div>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <SectionContainer className="relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-brand/5 opacity-0 duration-300 transition-opacity hover:opacity-100" />
              <div className="relative">
                <div className="mb-6">
                  <div className="mb-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-purple-100 dark:bg-purple-900/20">
                        <SparklesIcon className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                        Best Selling Prompts
                      </h3>
                    </div>
                    <AnchorLink
                      href={`${routes.marketplace}?refineBy=prompt`}
                      className="dark:text-brand-light text-sm font-medium text-brand hover:text-brand-dark dark:hover:text-brand"
                    >
                      See All →
                    </AnchorLink>
                  </div>
                  {/* <p className="text-sm text-gray-600 dark:text-gray-400">
                    Browse and buy expert-crafted prompts for immediate use.
                    Perfect for quick, consistent results.
                  </p> */}
                </div>
                <div className="overflow-hidden rounded-xl">
                  <ProductSlider
                    products={featuredProducts.data || []}
                    title=""
                    link="/products"
                    compact={true}
                  />
                </div>
              </div>
            </SectionContainer>
            <SectionContainer className="relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-brand/5 to-purple-500/5 opacity-0 duration-300 transition-opacity hover:opacity-100" />
              <div className="relative">
                <div className="mb-6">
                  <div className="mb-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand/10 dark:bg-brand/20">
                        <LightningBoltIcon className="h-5 w-5 text-brand" />
                      </div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                          Try AI Models
                        </h3>
                        <span className="animate-bounce rounded-md bg-red-600 px-1 py-0.5 text-[8px] font-medium text-white shadow-sm lg:text-[10px]">
                          New
                        </span>
                      </div>
                    </div>
                    <AnchorLink
                      href={`${routes.marketplace}?refineBy=model`}
                      className="dark:text-brand-light text-sm font-medium text-brand hover:text-brand-dark dark:hover:text-brand"
                    >
                      See All →
                    </AnchorLink>
                  </div>
                  {/* <p className="text-sm text-gray-600 dark:text-gray-400">
                    Use fine-tuned AI models to generate custom images. Perfect
                    for creating unique, personalized content.
                  </p> */}
                </div>
                <div className="overflow-hidden rounded-xl">
                  <TypesSection label="" />
                </div>
              </div>
            </SectionContainer>
          </div>
        </div>
      </section>

      <section className="container mx-auto max-w-[1280px] px-4 pb-8 sm:pb-12 md:pb-16">
        <div className="relative flex h-[350px] flex-col overflow-hidden rounded-3xl bg-brand dark:bg-brand-dark md:h-[400px] md:flex-row">
          <div className="absolute right-0 top-0 h-1/2 w-full md:h-full md:w-3/4">
            <Image
              src={ConsistentWomanImage}
              alt="AI Generation"
              layout="fill"
              objectFit="cover"
              quality={100}
              className="hidden object-center sm:block"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-brand-dark via-brand-dark/50 to-transparent md:bg-gradient-to-r"></div>
          </div>

          <div className="relative z-10 flex w-full flex-col justify-center p-6 sm:p-8 md:w-5/12 md:p-12">
            <h2 className="mb-4 text-3xl font-bold leading-tight tracking-tight text-white sm:mb-6 sm:text-4xl">
              Want To Create Your
              <br />
              Consistent AI Model?
            </h2>

            <div className="mb-6 flex flex-col items-start gap-4 sm:mb-8 sm:flex-row sm:items-center sm:gap-8">
              <div className="flex items-center gap-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-white/10 backdrop-blur-sm sm:h-10 sm:w-10">
                  <Upload className="h-4 w-4 text-white sm:h-5 sm:w-5" />
                </div>
                <p className="text-xs font-medium text-white/90 sm:text-sm">
                  Upload Photos
                </p>
              </div>

              <div className="flex items-center gap-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-md bg-white/10 backdrop-blur-sm sm:h-10 sm:w-10">
                  <Zap className="h-4 w-4 text-white sm:h-5 sm:w-5" />
                </div>
                <p className="text-xs font-medium text-white/90 sm:text-sm">
                  Generate Content
                </p>
              </div>
            </div>

            <AnchorLink href={routes.myPersonalAI}>
              <Button
                size="lg"
                className="relative flex items-center rounded-lg bg-secondary py-2 px-4 text-sm font-bold text-white shadow-xl hover:bg-secondary-dark sm:py-3 sm:px-6 sm:text-base"
              >
                <LightningBoltIcon className="mr-2 h-4 w-4 sm:h-5 sm:w-5" />
                Create Your AI Model
                <span className="absolute -top-3 -right-0 animate-bounce rounded-full bg-red-600 px-2 py-1 text-xs font-bold text-white shadow-lg">
                  Now with Flux!
                </span>
              </Button>
            </AnchorLink>
          </div>
        </div>
      </section>

      <section className="flex w-full items-center justify-center">
        <div className="container px-4 text-center md:px-6">
          <ProductSlider
            products={newProducts.data || []}
            title="New Prompts"
            link="/products?orderBy=new"
          />
          <ProductSlider
            products={popularChatGPTPrompts.data || []}
            title="ChatGPT Prompts"
            link="/products?aiModels=gpt"
          />
        </div>
      </section>

      <section className="container mx-auto max-w-[1280px] px-4">
        <div className="relative flex h-[350px] flex-col overflow-hidden rounded-3xl bg-brand dark:bg-brand-dark md:h-[400px] md:flex-row">
          <div className="absolute right-0 top-0 h-1/2 w-full md:h-full md:w-3/4">
            <Image
              src={ConsistentCharactersImage}
              alt="Sell Prompts"
              layout="fill"
              objectFit="cover"
              quality={100}
              className="hidden object-center sm:block"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-brand-dark via-brand-dark/50 to-transparent md:bg-gradient-to-r"></div>
          </div>

          <div className="relative z-10 flex w-full flex-col justify-center p-6 sm:p-8 md:w-5/12 md:p-12">
            <h2 className="mb-4 text-3xl font-bold leading-tight tracking-tight text-white sm:mb-6 sm:text-4xl">
              Earn Passive Income
              <br />
              on Prompts Club
            </h2>

            <div className="mb-6 flex flex-col items-start gap-4 sm:mb-8 sm:gap-8">
              <div className="flex items-center gap-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-white/10 backdrop-blur-sm sm:h-10 sm:w-10">
                  <SparklesIcon className="h-4 w-4 text-white sm:h-5 sm:w-5" />
                </div>
                <p className="text-xs font-medium text-white/90 sm:text-sm">
                  Publish your AI Prompt or Model
                </p>
              </div>
            </div>

            <AnchorLink href={routes.startSelling}>
              <Button
                size="lg"
                className="relative flex items-center rounded-lg bg-secondary py-2 px-4 text-sm font-bold text-white shadow-xl hover:bg-secondary-dark sm:py-3 sm:px-6 sm:text-base"
              >
                <DollarSign className="mr-2 h-4 w-4 sm:h-5 sm:w-5" />
                Become a seller
              </Button>
            </AnchorLink>
          </div>
        </div>
      </section>

      <section className="flex w-full items-center justify-center py-8 sm:py-12 md:py-16">
        <div className="container px-4 text-center md:px-6">
          <ProductSlider
            products={artIllustrationPrompts.data || []}
            title="Art & Illustration"
            link="/products?categories=art-and-design"
          />
          <ProductSlider
            products={logoIconPrompts.data || []}
            title="Logos & Icons"
            link="/products?categories=art-and-design/graphic-design/logo-and-icons"
          />
          <ProductSlider
            products={portraitPrompts.data || []}
            title="Photography"
            link="/products?categories=photography-and-video"
          />
        </div>
      </section>
    </>
  );
};

Home.getLayout = function getLayout(page) {
  return <Layout>{page}</Layout>;
};

export default Home;
